import { useState } from "react";
import { Link } from "react-router-dom";
import { queryString } from "lib/adminSiteApi";
import {
  Badge,
  ErrorNotice,
  Loading,
  Record,
  label,
  useRemote,
} from "./Controls";
import { FILTERS, SEARCH, CMS, endpoint, titleOf } from "./catalog";
import { useSplitLink } from "components/ios/SplitView";
export function Collection({
  domain,
  path,
  params = {},
  refresh = 0,
  selectRow,
  extra,
  compact = false,
  selectedId,
  split = false,
  keep = false,
}) {
  const [draft, setDraft] = useState({}),
    [filters, setFilters] = useState({}),
    [cursor, setCursor] = useState(null),
    [pages, setPages] = useState([]);
  // Only the split view's own list gets row links that swap the card; tables inside the card
  // (related records, lookups) keep ordinary links.
  const splitProps = useSplitLink(selectedId), link = split ? splitProps : {};
  const prefix = filters.prefix?.trim(),
    search = !!prefix;
  const base = path || (search ? `/admin/${domain}/search` : endpoint(domain));
  const query = search
    ? { prefix, ...(cursor || {}), limit: 25 }
    : {
        ...params,
        ...filters,
        ...(cursor ? { after: cursor } : {}),
        limit: 25,
      };
  const state = useRemote(`${base}?${queryString(query)}`, refresh, { keep });
  const allowed = CMS.includes(domain)
    ? ["status", "slug"]
    : FILTERS[domain] || [];
  const apply = (e) => {
    e.preventDefault();
    setFilters(draft.prefix?.trim() ? { prefix: draft.prefix } : draft);
    setCursor(null);
    setPages([]);
  };
  return (
    <section className="aw-panel">
      <div className="aw-row">
        <h2>{compact ? "Related records" : "Records"}</h2>
        {extra}
      </div>
      {!compact && (
        <form className="aw-filter" onSubmit={apply}>
          {SEARCH.includes(domain) && !path && (
            <label>
              Name or title starts with
              <input
                minLength={2}
                value={draft.prefix || ""}
                onChange={(e) => setDraft({ prefix: e.target.value })}
                placeholder="At least two characters"
              />
            </label>
          )}
          {allowed.slice(0, 2).map((key) => (
            <label key={key}>
              {label(key)}
              <input
                value={draft[key] || ""}
                onChange={(e) =>
                  setDraft({ ...draft, prefix: "", [key]: e.target.value })
                }
              />
            </label>
          ))}
          <button className="aw-button" type="submit">
            Apply filters
          </button>
          <button
            className="aw-button"
            type="button"
            onClick={() => {
              setDraft({});
              setFilters({});
              setCursor(null);
              setPages([]);
            }}
          >
            Reset
          </button>
        </form>
      )}
      <Loading state={state}>
        {state.data?.items?.length ? (
          <div
            className="aw-table-wrap"
            role="region"
            tabIndex={0}
            aria-label={`${label(domain)} table; scroll horizontally for more columns`}
          >
            <table>
              <caption className="aw-sr">{label(domain)} records</caption>
              <thead>
                <tr>
                  <th scope="col">Record</th>
                  <th scope="col" data-priority="1">Status / type</th>
                  <th scope="col" data-priority="3">Updated / created</th>
                  <th scope="col" data-priority={selectRow ? undefined : "3"}>Action</th>
                </tr>
              </thead>
              <tbody>
                {state.data.items.map((row) => (
                  <tr
                    key={row.id}
                    data-split-row={split ? "" : undefined}
                    data-selected={row.id === selectedId ? "true" : undefined}
                  >
                    <th scope="row">
                      {selectRow ? (
                        <span>{titleOf(row)}</span>
                      ) : (
                        <Link
                          className="aw-link"
                          to={`/admin/${domain}/${row.id}`}
                          aria-current={row.id === selectedId ? "true" : undefined}
                          {...link}
                        >
                          {titleOf(row)}
                        </Link>
                      )}
                      <small>{row.id}</small>
                    </th>
                    <td data-priority="1">
                      <Badge
                        value={
                          row.status ??
                          (row.active === undefined
                            ? row.kind || row.role
                            : row.active
                              ? "active"
                              : "inactive")
                        }
                      />
                    </td>
                    <td data-priority="3">{date(row.updatedAt || row.createdAt || row.at)}</td>
                    <td data-priority={selectRow ? undefined : "3"}>
                      {selectRow ? (
                        <button
                          className="aw-button aw-small"
                          onClick={() => selectRow(row)}
                        >
                          Select
                        </button>
                      ) : (
                        <Link
                          className="aw-link"
                          to={`/admin/${domain}/${row.id}`}
                          replace={link.replace}
                          state={link.state}
                        >
                          Open<span className="aw-sr"> {titleOf(row)}</span>
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="aw-empty">No records match this view.</p>
        )}
      </Loading>
      <div className="aw-row aw-pagination">
        <button
          className="aw-button"
          disabled={state.loading || !pages.length}
          onClick={() => {
            setCursor(pages[pages.length - 1]);
            setPages(pages.slice(0, -1));
          }}
        >
          Previous
        </button>
        <span>Page {pages.length + 1}</span>
        <button
          className="aw-button"
          disabled={state.loading || !state.data?.next}
          onClick={() => {
            setPages([...pages, cursor]);
            setCursor(state.data.next);
          }}
        >
          Next
        </button>
      </div>
    </section>
  );
}
export function date(value) {
  if (!value) return "—";
  const d = new Date(value?._seconds ? value._seconds * 1000 : value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}
export function RelatedRecords({ title, path, refresh = 0, actions }) {
  const [cursor, setCursor] = useState(null),
    [page, setPage] = useState(0);
  const state = useRemote(
    `${path}${path.includes("?") ? "&" : "?"}limit=25${cursor ? `&after=${encodeURIComponent(cursor)}` : ""}`,
    refresh,
  );
  return (
    <section className="aw-panel">
      <h2>{title}</h2>
      <Loading state={state}>
        {state.data?.items?.length ? (
          state.data.items.map((row) => (
            <article className="aw-subrecord" key={row.id}>
              <Record data={row} />
              {actions?.(row)}
            </article>
          ))
        ) : (
          <p className="aw-empty">None recorded.</p>
        )}
      </Loading>
      <div className="aw-row">
        <button
          className="aw-button"
          disabled={!page}
          onClick={() => {
            setCursor(null);
            setPage(0);
          }}
        >
          First page
        </button>
        <button
          className="aw-button"
          disabled={!state.data?.next || state.loading}
          onClick={() => {
            setCursor(state.data.next);
            setPage(page + 1);
          }}
        >
          Next page
        </button>
      </div>
    </section>
  );
}
export function Lookup({
  domain,
  onSelect,
  path,
  label: caption = "Find a record",
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="aw-lookup">
      <button
        type="button"
        className="aw-button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {caption}
      </button>
      {open && (
        <Collection
          domain={domain}
          path={path}
          selectRow={(row) => {
            onSelect(row);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}
export function ReadOnly({ path, refresh = 0 }) {
  const state = useRemote(path, refresh);
  return (
    <Loading state={state}>
      <Record data={state.data} />
    </Loading>
  );
}
export function SimpleAction({ title, action, onDone, disabled }) {
  const [state, setState] = useState({
    busy: false,
    error: null,
    success: false,
  });
  return (
    <div>
      <button
        className="aw-button"
        disabled={disabled || state.busy}
        onClick={async () => {
          setState({ busy: true });
          try {
            await action();
            setState({ success: true, busy: false });
            onDone?.();
          } catch (error) {
            setState({ busy: false, error });
          }
        }}
      >
        {state.busy ? "Processing…" : title}
      </button>
      <ErrorNotice error={state.error} />
      {state.success && <p role="status">Action completed.</p>}
    </div>
  );
}
